'use client'

import { usePermissions } from '@/features/application/hooks/use-permissions'
import { useSession } from '@/utils/auth/auth-client'

import { useCallback, useState } from 'react'

import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'

import { usePhoneCallLive } from '../hooks/phone-call-live'
import { isPhoneWindowPath } from '../hooks/phone-window'
import {
  DockContext,
  PhoneContext,
  PhoneStateContexts,
  usePhoneState,
  type ServiceCenterPhone
} from './service-center-phone-context'

// These load only for a user who may take calls. This module sits in the
// root layout, so it imports nothing of the phone itself: everyone else, and
// every public page, downloads none of them
// (service-center-phone-bundle.test.ts). The phone window loads the phone;
// Atlas tabs load only its mirror.
const ServiceCenterPhoneRuntime = dynamic(
  () =>
    import('./service-center-phone-runtime').then(
      (m) => m.ServiceCenterPhoneRuntime
    ),
  { ssr: false }
)
const ServiceCenterRemotePhoneRuntime = dynamic(
  () =>
    import('./service-center-remote-phone-runtime').then(
      (m) => m.ServiceCenterRemotePhoneRuntime
    ),
  { ssr: false }
)
const ServiceCenterFloatingPhone = dynamic(
  () =>
    import('./service-center-floating-phone').then(
      (m) => m.ServiceCenterFloatingPhone
    ),
  { ssr: false }
)

/**
 * The agent's phone, mounted once in the root layout so every Atlas route
 * shows it. The phone itself (the Twilio Device, the agent stream and with
 * it presence, the live call) runs only in the phone window
 * (phone-window.ts), so neither switching apps nor reloading an Atlas tab
 * touches a call; Atlas tabs mirror the window and send it the agent's
 * actions.
 *
 * Who gets it is unchanged: a signed-in support user (Internal access) with
 * service-center:calls:handle who holds the phone. Inside Internal the
 * sidebar dock shows it, in the phone window its page does, and everywhere
 * else a floating phone does. A live call keeps it all mounted whatever
 * happens to the session or the grants.
 *
 * The provider also owns what the agent entered for the current call (hold,
 * escalation, wrap-up) and which call's popover they closed, so views can
 * come and go without losing either; both are dropped once the desktop
 * moves past that call.
 */
export function ServiceCenterPhoneProvider({
  children
}: {
  children: React.ReactNode
}) {
  const { data: session, isPending: sessionPending } = useSession()
  const { can, isLoading: permissionsLoading } = usePermissions()
  // Fixed for the page's life: a link followed inside the phone window
  // changes the path, and must never swap the phone for a mirror mid-call.
  const pathname = usePathname()
  const [inPhoneWindow] = useState(() => isPhoneWindowPath(pathname))
  const callLive = usePhoneCallLive()
  const [phone, setPhone] = useState<ServiceCenterPhone | null>(null)
  // Whether the runtime is mounted, as it reports itself.
  const [runtimeUp, setRuntimeUp] = useState(false)
  const settled = !sessionPending && !permissionsLoading
  const eligible =
    Boolean(session?.user) &&
    can('internal', 'read') &&
    can('service-center:calls', 'handle')
  // While the session or the grants reload (an org switch resets both), a
  // mounted phone stays up (Web Lock included) rather than being torn down
  // and rebuilt; the reloaded grants decide once they are in. A live call
  // keeps it whatever they say.
  const runtimeMounted = callLive || (settled ? eligible : runtimeUp)
  const allowed = !settled || eligible

  const state = usePhoneState(runtimeMounted)
  const { settleCall } = state
  const publish = useCallback(
    (next: ServiceCenterPhone | null) => {
      setPhone(next)
      settleCall(next?.desktop.callId ?? null)
    },
    [settleCall]
  )

  const [docks, setDocks] = useState(0)
  const registerDock = useCallback(() => {
    setDocks((n) => n + 1)
    return () => setDocks((n) => n - 1)
  }, [])

  return (
    <PhoneContext.Provider value={phone}>
      <DockContext.Provider value={registerDock}>
        <PhoneStateContexts state={state}>
          {children}
          {runtimeMounted &&
            (inPhoneWindow ? (
              <ServiceCenterPhoneRuntime
                allowed={allowed}
                onChange={publish}
                onMounted={setRuntimeUp}
              />
            ) : (
              <ServiceCenterRemotePhoneRuntime
                allowed={allowed}
                onChange={publish}
                onMounted={setRuntimeUp}
              />
            ))}
          {phone && docks === 0 && <ServiceCenterFloatingPhone />}
        </PhoneStateContexts>
      </DockContext.Provider>
    </PhoneContext.Provider>
  )
}
